/**
 * WordOfTheDay – a warm daily micro-lesson card for Home.
 *
 * Fetches /api/word-of-day which is cached server-side per (user, UTC date).
 * Offers a "Hear it" button and a "Save" button, plus a fun fact.
 */
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkle, SpeakerHigh, SpinnerGap, BookmarkSimple, CheckCircle } from '@phosphor-icons/react';
import { http, API, apiErrorMessage, getStoredToken } from '@/lib/api';

export default function WordOfTheDay() {
  const [wod, setWod] = useState(null);
  const [error, setError] = useState('');
  const [playing, setPlaying] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get('/word-of-day');
        setWod(data);
      } catch (e) {
        setError(apiErrorMessage(e));
      }
    })();
  }, []);

  async function speak() {
    if (!wod?.term) return;
    setPlaying(true);
    try {
      const resp = await fetch(`${API}/tts`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(getStoredToken() ? { Authorization: `Bearer ${getStoredToken()}` } : {}),
        },
        body: JSON.stringify({ text: wod.term }),
      });
      if (!resp.ok) throw new Error('TTS failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.onended = () => { URL.revokeObjectURL(url); setPlaying(false); };
      audio.onerror = () => { URL.revokeObjectURL(url); setPlaying(false); };
      audio.play().catch(() => setPlaying(false));
    } catch { setPlaying(false); }
  }

  async function save() {
    if (!wod?.term) return;
    try {
      await http.post('/vocabulary/save', {
        items: [{ term: wod.term, translation: wod.translation, example: wod.example_target || '' }],
      });
      setSaved(true);
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  }

  if (error || !wod) return null; // silent on failure — it's a bonus card

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-6 lf-card overflow-hidden"
      data-testid="home-word-of-day"
    >
      <div className="relative p-5">
        <div className="absolute right-4 top-4">
          <Sparkle size={20} weight="fill" className="text-[var(--lf-accent)]" />
        </div>
        <div className="text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">Word of the day</div>
        <div className="mt-1 text-2xl font-bold text-[var(--lf-text)]" data-testid="wod-term">{wod.term}</div>
        {wod.pronunciation_respelling ? (
          <div className="mt-0.5 text-sm text-[var(--lf-text-muted)]">
            <span className="font-semibold">{wod.pronunciation_respelling}</span>
          </div>
        ) : null}
        <div className="mt-2 text-sm text-[var(--lf-text)]"><b>{wod.translation}</b> — {wod.definition}</div>
        {wod.example_target ? (
          <div className="mt-2 rounded-2xl bg-[var(--lf-surface-muted)] p-3">
            <div className="italic text-sm">“{wod.example_target}”</div>
            {wod.example_native ? (
              <div className="mt-0.5 text-xs text-[var(--lf-text-muted)]">{wod.example_native}</div>
            ) : null}
          </div>
        ) : null}
        {wod.fun_fact ? (
          <div className="mt-2 text-xs italic text-[var(--lf-text-muted)]">
            💡 {wod.fun_fact}
          </div>
        ) : null}
        <div className="mt-4 flex gap-2">
          <button
            onClick={speak}
            disabled={playing}
            className="lf-btn lf-btn-secondary flex-1 py-2 text-sm"
            data-testid="wod-play-btn"
          >
            {playing ? <SpinnerGap size={14} className="animate-spin" /> : <SpeakerHigh size={14} weight="fill" />}
            Hear it
          </button>
          <button
            onClick={save}
            disabled={saved}
            className="lf-btn lf-btn-primary flex-1 py-2 text-sm"
            data-testid="wod-save-btn"
          >
            {saved ? <CheckCircle size={14} weight="fill" /> : <BookmarkSimple size={14} weight="fill" />}
            {saved ? 'Saved' : 'Save'}
          </button>
        </div>
      </div>
    </motion.div>
  );
}
